import moment from 'moment';
import * as yup from 'yup';
import type {
    RppPrizeCoupon,
    RppPrizeCouponItem,
    RppPrizeCouponLanguage,
    RppPrizeCouponPayload,
    RppPrizeCouponType,
} from '../stores/rpp-prize-coupon';
import {
    convertLocalToUtcString,
    convertUtcToLocalMoment,
    getUserTimezoneOrFallback,
} from './timezone';

/** Per-language content as edited in the form (always rendered for `en` and `fr`). */
export interface RppPrizeCouponFormItem {
    language: RppPrizeCouponLanguage;
    title: string;
    subTitle: string;
    description: string;
    thumbnail: string | null;
    background: string | null;
}

/** Local form state for the create/edit screen. Dates are moment objects so the
 * `DatePicker` can edit wall-clock time; they are converted to/from UTC at the
 * network boundary using the coupon's timezone. */
export interface RppPrizeCouponForm {
    type: RppPrizeCouponType;
    timezone: string;
    startDate: moment.Moment | null;
    endDate: moment.Moment | null;
    isActive: boolean;
    isPrivate: boolean;
    activationCode: string;
    quantity: number;
    categoryId: number | undefined;
    promotionalOfferId: number | null;
    provinceIds: number[];
    discountValue: number | null;
    rebateAmount: number;
    originalPrice: number;
    items: RppPrizeCouponFormItem[];
}

function emptyItem(language: RppPrizeCouponLanguage): RppPrizeCouponFormItem {
    return {
        language,
        title: '',
        subTitle: '',
        description: '',
        thumbnail: null,
        background: null,
    };
}

export function createInitialForm(): RppPrizeCouponForm {
    return {
        // Default to `coupon` to preserve the existing behaviour.
        type: 'coupon',
        timezone: getUserTimezoneOrFallback(),
        startDate: moment().startOf('day'),
        endDate: moment().endOf('day'),
        isActive: true,
        isPrivate: true,
        activationCode: '',
        quantity: 1,
        categoryId: undefined,
        promotionalOfferId: null,
        provinceIds: [],
        discountValue: null,
        rebateAmount: 0,
        originalPrice: 0,
        items: [emptyItem('en'), emptyItem('fr')],
    };
}

/** Always produce an `en` + `fr` pair so both columns render, filling blanks for
 * any language the backend didn't return. */
export function mergeItems(
    items: RppPrizeCouponItem[]
): RppPrizeCouponFormItem[] {
    return (['en', 'fr'] as RppPrizeCouponLanguage[]).map((language) => {
        const found = items.find((item) => item.language === language);
        if (!found) {
            return emptyItem(language);
        }
        return {
            language,
            title: found.title ?? '',
            subTitle: found.subTitle ?? '',
            description: found.description ?? '',
            thumbnail: found.thumbnail ?? null,
            background: found.background ?? null,
        };
    });
}

export function couponToForm(coupon: RppPrizeCoupon): RppPrizeCouponForm {
    const timezone = coupon.timezone || getUserTimezoneOrFallback();
    return {
        type: coupon.type ?? 'coupon',
        timezone,
        startDate: convertUtcToLocalMoment(coupon.startDate, timezone),
        endDate: convertUtcToLocalMoment(coupon.endDate, timezone),
        isActive: coupon.isActive,
        isPrivate: coupon.isPrivate ?? true,
        activationCode: coupon.activationCode ?? '',
        quantity: coupon.quantity ?? 1,
        categoryId: coupon.categoryId ?? undefined,
        promotionalOfferId: coupon.promotionalOfferId,
        provinceIds: coupon.provinceIds ?? [],
        discountValue: coupon.discountValue,
        rebateAmount: coupon.rebateAmount ?? 0,
        originalPrice: coupon.originalPrice ?? 0,
        items: mergeItems(coupon.items ?? []),
    };
}

function trimToNull(value: string): string | null {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
}

/** Build the items payload: `en` is always sent; `fr` is sent only when it has a
 * title (the backend requires every item to have a title, and `fr` is optional). */
export function buildItems(
    items: RppPrizeCouponFormItem[]
): RppPrizeCouponItem[] {
    return items
        .filter(
            (item) => item.language === 'en' || item.title.trim().length > 0
        )
        .map((item) => ({
            language: item.language,
            title: item.title.trim(),
            subTitle: trimToNull(item.subTitle),
            description:
                item.description.trim().length > 0 ? item.description : null,
            thumbnail: item.thumbnail || null,
            background: item.background || null,
        }));
}

// The province tree injects a string "SELECT_ALL" sentinel when every province
// is selected; keep only real numeric province ids before persisting.
export function cleanProvinceIds(ids: number[]): number[] {
    return ids.filter((id) => Number.isInteger(id));
}

export function buildPayload(
    values: RppPrizeCouponForm
): RppPrizeCouponPayload {
    const { timezone } = values;
    const payload: RppPrizeCouponPayload = {
        type: values.type,
        startDate: convertLocalToUtcString(values.startDate, timezone) ?? '',
        endDate: convertLocalToUtcString(values.endDate, timezone) ?? '',
        timezone,
        isActive: values.isActive,
        categoryId: (values.categoryId ?? 0) as number,
        promotionalOfferId: values.promotionalOfferId ?? null,
        provinceIds: cleanProvinceIds(values.provinceIds),
        discountValue: values.discountValue,
        rebateAmount: values.rebateAmount,
        originalPrice: values.originalPrice,
        items: buildItems(values.items),
    };

    // Coupon-only prize/inventory fields. For in-store promos we omit them
    // entirely (rather than forwarding any stale form values) so the backend
    // treats them as public display promos with no inventory.
    if (values.type === 'coupon') {
        payload.isPrivate = values.isPrivate;
        payload.activationCode = values.activationCode;
        payload.quantity = values.quantity;
    }

    return payload;
}

export const YupRppPrizeCouponForm = yup.object({
    type: yup
        .string()
        .oneOf(['coupon', 'in_store'], 'Type is required.')
        .required('Type is required.'),
    timezone: yup.string().trim().required('Timezone is required.'),
    startDate: yup
        .mixed<moment.Moment>()
        .test('required', 'Start date is required.', (value) => value != null),
    endDate: yup
        .mixed<moment.Moment>()
        .test('required', 'End date is required.', (value) => value != null)
        .test(
            'end-after-start',
            'End date must be on or after the start date.',
            function (endDate) {
                const startDate = this.parent.startDate as
                    | moment.Moment
                    | null
                    | undefined;
                if (!startDate || !endDate) {
                    return true;
                }
                return moment(endDate as moment.Moment).isSameOrAfter(
                    moment(startDate)
                );
            }
        ),
    isActive: yup.boolean().required(),
    // Coupon-only fields: required for `coupon`, not required for `in_store`
    // public promos (which have no privacy flag, activation code or inventory).
    isPrivate: yup.boolean().when('type', {
        is: 'coupon',
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.notRequired(),
    }),
    activationCode: yup.string().when('type', {
        is: 'coupon',
        then: (schema) =>
            schema
                .required('Activation code is required.')
                .matches(
                    /^\d{12}$/,
                    'Activation code must be exactly 12 digits.'
                ),
        otherwise: (schema) => schema.notRequired(),
    }),
    quantity: yup.number().when('type', {
        is: 'coupon',
        then: (schema) =>
            schema
                .typeError('Quantity is required.')
                .required('Quantity is required.')
                .min(1, 'Quantity must be at least 1.'),
        otherwise: (schema) => schema.notRequired(),
    }),
    categoryId: yup
        .number()
        .typeError('Category is required.')
        .required('Category is required.')
        .min(1, 'Category is required.'),
    promotionalOfferId: yup.number().nullable(),
    provinceIds: yup
        .array()
        .of(yup.number().required())
        .min(1, 'Select at least one province.')
        .required(),
    discountValue: yup
        .number()
        .nullable()
        .min(0, 'Discount value must be 0 or more.'),
    rebateAmount: yup
        .number()
        .required()
        .min(0, 'Rebate amount must be 0 or more.'),
    originalPrice: yup
        .number()
        .required()
        .min(0, 'Original price must be 0 or more.'),
    items: yup
        .array()
        .of(
            yup.object({
                language: yup.string().oneOf(['en', 'fr']).required(),
                title: yup.string(),
                subTitle: yup.string(),
                description: yup.string(),
                thumbnail: yup.string().nullable(),
                background: yup.string().nullable(),
            })
        )
        .required()
        .test('en-title-required', 'An English title is required.', (items) => {
            const en = (items ?? []).find((item) => item?.language === 'en');
            return (
                !!en &&
                typeof en.title === 'string' &&
                en.title.trim().length > 0
            );
        }),
});
